import { PRODUCT_MAP } from "./products";
import { pairRule } from "./safety";

const MAX_LEAVES = 150000;

const isEnd = (i, n) => i === 0 || i === n - 1;

// Front and rear pockets should be full or empty, so a product fills its end pockets first.
// Under 90% counts as partly full; a nearly-full pocket barely sloshes.
export const partialEnds = (compartments, loads) =>
  loads.map((l, i) => i).filter((i) => isEnd(i, loads.length) && loads[i].product && loads[i].gallons < compartments[i].capacity * 0.9);

const fill = (compartments, assign, prods) => {
  const loads = compartments.map(() => ({ product: null, gallons: 0 }));
  const shortfalls = [];
  const n = compartments.length;
  prods.forEach((p, k) => {
    const idxs = assign.map((a, i) => (a === k ? i : -1)).filter((i) => i >= 0)
      .sort((a, b) => isEnd(a, n) - isEnd(b, n) ? isEnd(b, n) - isEnd(a, n) : compartments[a].capacity - compartments[b].capacity);
    let remaining = p.gallons;
    const middleRoom = idxs.filter((i) => !isEnd(i, n)).reduce((a, i) => a + compartments[i].capacity, 0);
    const skipped = [];
    const put = (i) => {
      const g = Math.min(compartments[i].capacity, remaining);
      if (g > 0) loads[i] = { product: p.product, gallons: g, label: p.label, line: p.key };
      remaining -= g;
    };
    for (const i of idxs) {
      // An end pocket that would end up partly full is skipped if the middle pockets can take the rest.
      if (isEnd(i, n) && remaining < compartments[i].capacity && middleRoom >= remaining) { skipped.push(i); continue; }
      put(i);
    }
    skipped.forEach(put);
    if (remaining > 0) shortfalls.push({ product: p.product, gallons: remaining, label: p.label });
  });
  return { loads, shortfalls };
};

// Extra runs per product, ignoring empty/dry pockets: only a different product in between splits it.
export const countSplits = (loads) => {
  const seq = loads.map((l) => l.product).filter(Boolean);
  const runs = {};
  seq.forEach((p, i) => { if (seq[i - 1] !== p) runs[p] = (runs[p] || 0) + 1; });
  return Object.values(runs).reduce((a, n) => a + n - 1, 0);
};

// Score order: unmet gallons, unsafe neighbors, half-full front/rear pockets, [split products], pockets used, cautions, empty space.
const score = (compartments, r, group) => {
  let high = 0, med = 0, pockets = 0, space = 0, touching = 0;
  r.loads.forEach((l, i) => {
    if (l.product) { pockets++; space += compartments[i].capacity - l.gallons; }
    const prev = i > 0 && r.loads[i - 1].product;
    if (prev && l.product && prev !== l.product) touching++;
    const rule = i > 0 && pairRule(prev, l.product);
    if (rule) rule.level === "high" ? high++ : med++;
  });
  return [r.shortfalls.reduce((a, s) => a + s.gallons, 0), high, partialEnds(compartments, r.loads).length, group ? countSplits(r.loads) : 0, pockets, med, touching, space];
};

const better = (a, b) => {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] < b[i];
  return false;
};

// lines: [{ key, product, gallons, label }] — one line per product, or per customer tank.
// Exhaustive search (one line per pocket); falls back to mask DP for very large trailers.
export function allocate(compartments, lines, { group = false, dieselDouble = false } = {}) {
  const prods = lines.filter((l) => l.gallons > 0).map((l) => ({ ...l, group: PRODUCT_MAP[l.product].group }));
  const open = compartments.map((c, i) => i).filter((i) => !compartments[i].locked_dry);
  const allowed = (k, idx) => !(dieselDouble && prods[k].group === "diesel" && !compartments[idx].double_bulkhead);
  if (Math.pow(prods.length + 1, open.length) > MAX_LEAVES) return dpAllocate(compartments, prods, open, allowed);

  const assign = compartments.map(() => -1);
  let best = null, bestScore = null;
  const walk = (j) => {
    if (j === open.length) {
      const r = fill(compartments, assign, prods);
      const s = score(compartments, r, group);
      if (!bestScore || better(s, bestScore)) { best = r; bestScore = s; }
      return;
    }
    for (let k = -1; k < prods.length; k++) {
      if (k >= 0 && !allowed(k, open[j])) continue;
      assign[open[j]] = k;
      walk(j + 1);
    }
    assign[open[j]] = -1;
  };
  walk(0);
  return best;
}

const popcount = (m) => { let c = 0; while (m) { m &= m - 1; c++; } return c; };

function dpAllocate(compartments, prods, open, allowed) {
  const n = open.length, size = 1 << n, full = size - 1;
  const sums = new Array(size).fill(0);
  for (let m = 1; m < size; m++) {
    const low = m & -m;
    sums[m] = sums[m ^ low] + compartments[open[31 - Math.clz32(low)]].capacity;
  }
  const cost = (order, sub) => Math.max(0, order - sums[sub]) * 1e9 + popcount(sub) * 1e6 + Math.max(0, sums[sub] - order);
  let dp = new Array(size).fill(Infinity);
  dp[0] = 0;
  const choices = [];
  for (const [k, p] of prods.entries()) {
    let okMask = 0;
    open.forEach((idx, b) => { if (allowed(k, idx)) okMask |= 1 << b; });
    const ndp = new Array(size).fill(Infinity);
    const choice = new Int32Array(size);
    for (let mask = 0; mask < size; mask++) {
      if (dp[mask] === Infinity) continue;
      const free = full & ~mask & okMask;
      for (let sub = free; ; sub = (sub - 1) & free) {
        const v = dp[mask] + cost(p.gallons, sub);
        if (v < ndp[mask | sub]) { ndp[mask | sub] = v; choice[mask | sub] = sub; }
        if (sub === 0) break;
      }
    }
    dp = ndp;
    choices.push(choice);
  }
  let mask = 0;
  for (let m = 0; m < size; m++) if (dp[m] < dp[mask]) mask = m;
  const assign = compartments.map(() => -1);
  for (let k = prods.length - 1; k >= 0; k--) {
    const sub = choices[k][mask];
    mask ^= sub;
    for (let b = 0; b < n; b++) if (sub & (1 << b)) assign[open[b]] = k;
  }
  return fill(compartments, assign, prods);
}
